// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF

import React from 'react';
import { CameraCalibration, UseCase } from '@tls/sw91-communication/types/com.base';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import HighSpeedImage from './HighSpeedImage';
import HighPrecisionImage from './HighPrecisionImage';
import { Headline, InputFieldValue } from '@tls/treact-ui';

interface Props {
  useCase: UseCase;
  setCameraCalibration: React.Dispatch<React.SetStateAction<CameraCalibration>>;
  cameraCalibration: CameraCalibration;
}

export const UseCaseAdditions = ({ useCase, setCameraCalibration, cameraCalibration }: Props) => {
  const { t } = useTranslation();

  return (
    <>
      {useCase === UseCase.COMPONENT_DETECTION && (
        <Description>
          <DescriptionItem>
            <div style={{ flex: 1 }}>
              <HighSpeedImage />
            </div>
            <div style={{ flex: 3 }}>
              <Headline as='h2' label={t('project.strategy.highSpeed')} />
              <div>{t('project.strategy.HighSpeedDescription')}</div>
            </div>
          </DescriptionItem>
          <DescriptionItem>
            <div style={{ flex: 1 }}>
              <HighPrecisionImage />
            </div>
            <div style={{ flex: 3 }}>
              <Headline as='h2' label={t('project.strategy.highPrecision')} />
              <div>{t('project.strategy.HighPrecisionDescription')}</div>
            </div>
          </DescriptionItem>
        </Description>
      )}

      {useCase === UseCase.QUALITY_CHECK && (
        <Container>
          <FourColumns>
            <InputFieldValue
              flag='none'
              helperText=''
              label={t('projects.create.qualityControl.xScaling')}
              onValueChange={value => {
                if (value !== null) setCameraCalibration(prev => ({ ...prev, scaleFactorX: value * 0.001 }));
              }}
              suffix='mm/pixels'
              value={
                cameraCalibration.scaleFactorX !== null
                  ? parseFloat((cameraCalibration.scaleFactorX * 1000).toFixed(10))
                  : null
              }
              data-testid='project-settings-x-scaling-input'
            />
            <InputFieldValue
              flag='none'
              helperText=''
              label={t('projects.create.qualityControl.yScaling')}
              onValueChange={value => {
                if (value !== null) setCameraCalibration(prev => ({ ...prev, scaleFactorY: value * 0.001 }));
              }}
              suffix='mm/pixels'
              value={
                cameraCalibration.scaleFactorY !== null
                  ? parseFloat((cameraCalibration.scaleFactorY * 1000).toFixed(10))
                  : null
              }
              data-testid='project-settings-y-scaling-input'
            />
          </FourColumns>
        </Container>
      )}
    </>
  );
};
const Container = styled.div`
  display: grid;
`;
const GridBase = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr) 38px;
  grid-gap: 8px;
`;

const FourColumns = styled(GridBase)``;

const Description = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.5rem;
  white-space: pre-line;
`;
const DescriptionItem = styled.div`
  gap: 0.5rem;
  justify-content: end;
  align-items: center;
  display: flex;
  flex: 2;
`;
